(() => {
  const mounted = new WeakMap();
  const waterMounted = new WeakMap();

  function mountWaterProblems(root = document) {
    root.querySelectorAll('[data-lus-water-problems]').forEach(section => {
      if (waterMounted.has(section)) return;
      const tabs = [...section.querySelectorAll('[data-lus-water-tab]')];
      const panels = [...section.querySelectorAll('[data-lus-water-panel]')];
      if (!tabs.length || tabs.length !== panels.length) return;
      const controller = new AbortController();
      const activate = (index, moveFocus = false) => {
        const next = (index + tabs.length) % tabs.length;
        tabs.forEach((tab, tabIndex) => {
          const active = tabIndex === next;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel, panelIndex) => {
          const active = panelIndex === next;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });
        if (moveFocus) tabs[next].focus();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(index), { signal: controller.signal });
        tab.addEventListener('keydown', event => {
          if (!['ArrowRight','ArrowDown','ArrowLeft','ArrowUp','Home','End'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Home') activate(0, true);
          else if (event.key === 'End') activate(tabs.length - 1, true);
          else activate(index + (event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1), true);
        }, { signal: controller.signal });
      });
      document.addEventListener('shopify:section:unload', event => {
        if (!event.target.contains(section)) return;
        controller.abort();
        waterMounted.delete(section);
      }, { signal: controller.signal });
      activate(0);
      section.classList.add('is-ready');
      waterMounted.set(section, controller);
    });
  }

  function mount(root = document) {
    root.querySelectorAll('[data-lus-scroll-story]').forEach(story => {
      if (mounted.has(story)) return;
      const steps = [...story.querySelectorAll('[data-lus-scroll-step]')];
      const mediaStates = [...story.querySelectorAll('[data-lus-scroll-media-state]')];
      const stickyShop = story.classList.contains('lus-scroll-story--system')
        ? story.parentElement?.querySelector('[data-lus-home-sticky-shop]')
        : null;
      const stickyStop = stickyShop ? document.querySelector('#before-you-choose') : null;
      if (!steps.length) return;

      let activeIndex = -1;
      let inRange = false;
      let scheduled = false;
      const controller = new AbortController();

      const syncStickyShop = () => {
        if (!stickyShop) return;
        const visible = element => {
          if (!element || !element.getClientRects().length) return false;
          const rect = element.getBoundingClientRect();
          const style = getComputedStyle(element);
          return style.visibility !== 'hidden' && style.display !== 'none'
            && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
        };
        const blocking = [...document.querySelectorAll('dialog[open],[role="dialog"],.menu-drawer,.drawer.active,cart-drawer.active,.cart-notification.active,.ecomsend__Modal')]
          .some(visible);
        const keyboard = /INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName)
          || (window.visualViewport && window.visualViewport.height < innerHeight * .75);
        const previewBar = document.querySelector('#PBarNextFrameWrapper,#preview-bar-iframe');
        const previewOffset = previewBar && previewBar.getClientRects().length
          ? Math.ceil(previewBar.getBoundingClientRect().height)
          : 0;
        document.documentElement.style.setProperty('--lus-preview-offset', `${previewOffset}px`);
        const triggerReached = story.getBoundingClientRect().top <= innerHeight * .72;
        const faqReached = stickyStop && stickyStop.getBoundingClientRect().top <= innerHeight;
        const show = triggerReached && !faqReached && !blocking && !keyboard;
        stickyShop.style.setProperty('--lus-preview-offset', `${previewOffset}px`);
        stickyShop.hidden = !show;
        document.documentElement.classList.toggle('lus-home-shop-visible', show);
      };

      const activate = index => {
        const next = Math.max(0, Math.min(index, steps.length - 1));
        if (next === activeIndex) return;
        activeIndex = next;
        story.dataset.activeStep = String(next);
        steps.forEach((step, stepIndex) => {
          const active = stepIndex === next;
          step.classList.toggle('is-active', active);
          if (active) step.setAttribute('aria-current', 'step');
          else step.removeAttribute('aria-current');
        });
        mediaStates.forEach((state, stateIndex) => {
          const active = stateIndex === next;
          state.classList.toggle('is-active', active);
          state.setAttribute('aria-hidden', String(!active));
        });
      };

      const update = () => {
        scheduled = false;
        syncStickyShop();
        if (!inRange) return;
        const target = innerHeight * .48;
        let closest = 0;
        let distance = Infinity;
        steps.forEach((step, index) => {
          const rect = step.getBoundingClientRect();
          const centre = rect.top + rect.height / 2;
          const candidate = Math.abs(centre - target);
          if (candidate < distance) {
            distance = candidate;
            closest = index;
          }
        });
        activate(closest);
      };

      const schedule = () => {
        if (!scheduled) {
          scheduled = true;
          requestAnimationFrame(update);
        }
      };

      const rangeObserver = new IntersectionObserver(entries => {
        inRange = entries[0]?.isIntersecting ?? false;
        if (inRange) {
          schedule();
          return;
        }

        const rect = story.getBoundingClientRect();
        if (rect.top >= innerHeight) activate(0);
        else if (rect.bottom <= 0) activate(steps.length - 1);
      }, { rootMargin: '25% 0px 25% 0px' });

      rangeObserver.observe(story);
      addEventListener('scroll', schedule, { passive: true, signal: controller.signal });
      addEventListener('resize', schedule, { passive: true, signal: controller.signal });
      document.addEventListener('shopify:section:unload', event => {
        if (!event.target.contains(story)) return;
        controller.abort();
        rangeObserver.disconnect();
        if (stickyShop) document.documentElement.classList.remove('lus-home-shop-visible');
        document.documentElement.style.removeProperty('--lus-preview-offset');
        mounted.delete(story);
      }, { signal: controller.signal });
      activate(0);
      story.classList.add('is-ready');
      syncStickyShop();
      mounted.set(story, { rangeObserver, controller });
    });
  }

  function mountAll(root = document) {
    mount(root);
    mountWaterProblems(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountAll());
  else mountAll();
  document.addEventListener('shopify:section:load', event => mountAll(event.target));
})();
